<template>
  <DataCard
    title="Paramètres du compte"
    icon="fas fa-user"
    icon-color="text-blue-600"
    icon-bg-color="bg-blue-100"
    class="w-full"
  >
    <!-- Bouton Sauvegarder aligné à droite de l'en-tête -->
    <template #header-actions>
      <button
        @click="$emit('save')"
        :disabled="saving || disableSave"
        class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        <i v-if="saving" class="fas fa-spinner fa-spin"></i>
        <i v-else class="fas fa-save"></i>
        <span>{{ saving ? "Sauvegarde..." : "Sauvegarder" }}</span>
      </button>
    </template>
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
      <!-- Colonne gauche -->
      <div class="space-y-8">
        <!-- Informations personnelles -->
        <div class="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center"
            >
              <i class="fas fa-user text-black text-sm"></i>
            </div>
            <h3 class="text-lg font-semibold text-black">
              Informations personnelles
            </h3>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BaseInput
              :model-value="formData.lastName"
              @update:model-value="updateFormData('lastName', $event)"
              label="Nom"
              type="text"
              placeholder="Votre nom"
              required
            />
            <BaseInput
              :model-value="formData.firstName"
              @update:model-value="updateFormData('firstName', $event)"
              label="Prénom"
              type="text"
              placeholder="Votre prénom"
              required
            />
            <BaseInput
              :model-value="formData.email"
              @update:model-value="updateFormData('email', $event)"
              label="Email"
              type="email"
              placeholder="votre@email.com"
              required
            />
            <BaseInput
              :model-value="formData.emailSecondary"
              @update:model-value="updateFormData('emailSecondary', $event)"
              label="Email 2"
              type="email"
              placeholder="email@secondaire.com"
            />
          </div>
        </div>

        <!-- Informations de commande et site web -->
        <div class="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center"
            >
              <i class="fas fa-shopping-cart text-black text-sm"></i>
            </div>
            <h3 class="text-lg font-semibold text-black">
              Informations de commande
            </h3>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BaseInput
              :model-value="formData.minOrder"
              @update:model-value="updateFormData('minOrder', $event)"
              label="Minimum de commande"
              type="number"
              min="0"
              step="0.01"
            />
            <BaseInput
              :model-value="formData.website"
              @update:model-value="updateFormData('website', $event)"
              label="Site web"
              type="text"
              placeholder="https://votre-site.com"
            />
          </div>
        </div>

        <!-- Réseaux sociaux -->
        <div class="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center"
            >
              <i class="fas fa-share-alt text-black text-sm"></i>
            </div>
            <h3 class="text-lg font-semibold text-black">Réseaux sociaux</h3>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BaseInput
              :model-value="formData.socialMedia.facebook"
              @update:model-value="
                updateFormData('socialMedia.facebook', $event)
              "
              label="Facebook"
              type="text"
              placeholder="https://facebook.com/votre-page"
            />
            <BaseInput
              :model-value="formData.socialMedia.twitter"
              @update:model-value="
                updateFormData('socialMedia.twitter', $event)
              "
              label="Twitter"
              type="text"
              placeholder="https://twitter.com/votre-compte"
            />
            <BaseInput
              :model-value="formData.socialMedia.instagram"
              @update:model-value="
                updateFormData('socialMedia.instagram', $event)
              "
              label="Instagram"
              type="text"
              placeholder="https://instagram.com/votre-compte"
            />
            <BaseInput
              :model-value="formData.socialMedia.tiktok"
              @update:model-value="updateFormData('socialMedia.tiktok', $event)"
              label="TikTok"
              type="text"
              placeholder="https://tiktok.com/votre-compte"
            />
          </div>
        </div>


        <!-- Informations bancaires -->
        <div class="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center"
            >
              <i class="fas fa-university text-black text-sm"></i>
            </div>
            <h3 class="text-lg font-semibold text-black">
              Informations bancaires
            </h3>
          </div>
          <div class="space-y-4">
            <!-- Titulaire du compte -->
            <div class="space-y-3">
              <label class="block text-sm font-medium text-black">
                Titulaire du compte
                <span class="text-red-500">*</span>
              </label>
              <div class="relative">
                <input
                  type="text"
                  placeholder="Nom du titulaire du compte"
                  :value="formData.banking.accountTitle"
                  @input="
                    updateFormData(
                      'banking.accountTitle',
                      ($event.target as HTMLInputElement).value,
                    )
                  "
                  @blur="validateAccountTitle"
                  :maxlength="255"
                  class="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                  :class="
                    accountTitleHasError || !formData.banking.accountTitle
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-300 hover:border-gray-400'
                  "
                />

                <!-- Message champ requis -->
                <p
                  v-if="!formData.banking.accountTitle"
                  class="text-sm text-red-500 mt-2 flex items-center"
                >
                  <i class="fas fa-exclamation-circle mr-2"></i>
                  Titulaire du compte obligatoire
                </p>
              </div>
            </div>

            <!-- IBAN avec validation comme register -->
            <div class="space-y-3">
              <label class="block text-sm font-medium text-black">
                IBAN
                <span class="text-red-500">*</span>
              </label>
              <div class="relative">
                <input
                  type="text"
                  placeholder="FR76 3000 1007 9416 0000 0000 000"
                  :value="ibanValue"
                  @input="handleIbanInput"
                  @blur="validateIban"
                  :maxlength="35"
                  class="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                  :class="
                    ibanHasError || !ibanValue
                      ? 'border-red-500 bg-red-50'
                      : ibanIsValid && ibanValue
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                  "
                />

                <!-- Message d'erreur -->
                <p
                  v-if="ibanHasError"
                  class="text-sm text-red-500 mt-2 flex items-center"
                >
                  <i class="fas fa-exclamation-circle mr-2"></i>
                  {{ ibanErrorMessage }}
                </p>

                <!-- Message champ requis -->
                <p
                  v-else-if="!ibanValue"
                  class="text-sm text-red-500 mt-2 flex items-center"
                >
                  <i class="fas fa-exclamation-circle mr-2"></i>
                  IBAN obligatoire
                </p>

                <!-- Message de succès -->
                <p
                  v-else-if="ibanIsValid && ibanValue"
                  class="text-sm text-green-600 mt-2 flex items-center"
                >
                  <i class="fas fa-check-circle mr-2"></i>
                  Format IBAN valide
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Colonne droite -->
      <div class="space-y-8">
        <!-- Gestion du mot de passe -->
        <div class="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center"
            >
              <i class="fas fa-lock text-black text-sm"></i>
            </div>
            <h3 class="text-lg font-semibold text-black">
              Gestion du mot de passe
            </h3>
          </div>
          <div class="space-y-4">
            <div
              class="flex items-center p-3 bg-white rounded-lg border border-gray-200"
            >
              <input
                :checked="formData.security.changePassword"
                @change="
                  updateFormData(
                    'security.changePassword',
                    ($event.target as HTMLInputElement).checked,
                  )
                "
                type="checkbox"
                id="changePassword"
                class="w-4 h-4 text-black bg-gray-100 border-gray-300 rounded focus:ring-gray-500"
              />
              <label
                for="changePassword"
                class="ml-3 text-sm font-medium text-black"
              >
                Changer le mot de passe
              </label>
            </div>
            <div
              v-if="formData.security.changePassword"
              class="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-white rounded-lg border border-gray-200"
            >
              <!-- Nouveau mot de passe avec bouton afficher/masquer -->
              <div class="relative">
                <BaseInput
                  :model-value="formData.security.newPassword"
                  @update:model-value="
                    updateFormData('security.newPassword', $event)
                  "
                  label="Nouveau mot de passe"
                  :type="newPwdType"
                  placeholder="Nouveau mot de passe"
                  :error="passwordError"
                />
                <button
                  type="button"
                  @click="showNewPassword = !showNewPassword"
                  class="absolute right-2 top-[20px] h-8 w-8 flex items-center justify-center text-black hover:text-black"
                  :aria-label="
                    showNewPassword
                      ? 'Masquer le mot de passe'
                      : 'Afficher le mot de passe'
                  "
                >
                  <i
                    :class="showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"
                  ></i>
                </button>
              </div>

              <!-- Confirmation mot de passe avec bouton afficher/masquer -->
              <div class="relative">
                <BaseInput
                  :model-value="formData.security.confirmPassword"
                  @update:model-value="
                    updateFormData('security.confirmPassword', $event)
                  "
                  label="Confirmer mot de passe"
                  :type="confirmPwdType"
                  placeholder="Confirmer mot de passe"
                  :error="confirmPasswordError"
                />
                <button
                  type="button"
                  @click="showConfirmPassword = !showConfirmPassword"
                  class="absolute right-2 top-[20px] h-8 w-8 flex items-center justify-center text-black hover:text-black"
                  :aria-label="
                    showConfirmPassword
                      ? 'Masquer le mot de passe'
                      : 'Afficher le mot de passe'
                  "
                >
                  <i
                    :class="
                      showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'
                    "
                  ></i>
                </button>
              </div>
            </div>
            <!-- Message d'erreur pour les mots de passe -->
            <div
              v-if="
                formData.security.changePassword &&
                (passwordError || confirmPasswordError)
              "
              class="mt-2 text-sm text-red-600"
            >
              {{ passwordError || confirmPasswordError }}
            </div>
          </div>
        </div>

        <!-- Statut de la boutique -->
        <div class="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center"
            >
              <i class="fas fa-store text-black text-sm"></i>
            </div>
            <h3 class="text-lg font-semibold text-black">
              Statut de la boutique
            </h3>
          </div>
          <div class="space-y-3">
            <div
              class="flex items-center p-3 bg-white rounded-lg border border-gray-200"
            >
              <input
                :checked="formData.shop.status === 'online'"
                @change="updateFormData('shop.status', 'online')"
                type="radio"
                id="online"
                value="online"
                class="w-4 h-4 text-black bg-gray-100 border-gray-300 focus:ring-gray-500"
              />
              <label
                for="online"
                class="ml-3 text-sm font-medium text-black"
              >
                En ligne
              </label>
            </div>
            <div
              class="flex items-center p-3 bg-white rounded-lg border border-gray-200"
            >
              <input
                :checked="formData.shop.status === 'offline'"
                @change="updateFormData('shop.status', 'offline')"
                type="radio"
                id="offline"
                value="offline"
                class="w-4 h-4 text-black bg-gray-100 border-gray-300 focus:ring-gray-500"
              />
              <label
                for="offline"
                class="ml-3 text-sm font-medium text-black"
              >
                Hors ligne
              </label>
            </div>
            <p class="text-sm text-black italic">
              Choisissez le statut hors ligne pendant que votre boutique n'est
              pas prête, ou en cas de modification majeure du catalogue...
            </p>
          </div>
        </div>

        <!-- Visibilité du catalogue -->
        <div class="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center"
            >
              <i class="fas fa-eye text-black text-sm"></i>
            </div>
            <h3 class="text-lg font-semibold text-black">
              Votre catalogue pour les clients non connectés
            </h3>
          </div>
          <div
            class="flex items-center gap-2 p-3 bg-white rounded-lg border border-gray-200"
          >
            <BaseInput
              :model-value="formData.shop.catalogVisibility"
              @update:model-value="
                updateFormData('shop.catalogVisibility', $event)
              "
              label=""
              type="select"
              :options="catalogVisibilityOptions"
              class="w-24"
            />
            <span class="text-sm text-black">mois</span>
          </div>
          <p class="text-sm text-black mt-3 italic">
            Vous pouvez désormais choisir à partir de combien de temps (en mois)
            après leur mise en ligne vos produits seront visibles à tous. Nous
            vous recommandons fortement d'afficher votre catalogue aux clients
            non connectés car cela aura pour effet d'optimiser la visibilité de
            votre marque sur notre site, et d'inciter de nouveaux clients à nous
            rejoindre.
          </p>
        </div>

        <!-- Notifications et affichage -->
        <div class="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center"
            >
              <i class="fas fa-sliders-h text-black text-sm"></i>
            </div>
            <h3 class="text-lg font-semibold text-black">
              Affichage
            </h3>
          </div>
          <div class="space-y-3">
            <!-- SMS de notification désactivé temporairement
            <div
              class="flex items-center p-3 bg-white rounded-lg border border-gray-200"
            >
              <input
                :checked="formData.shop.smsNotification"
                @change="
                  updateFormData(
                    'shop.smsNotification',
                    ($event.target as HTMLInputElement).checked,
                  )
                "
                type="checkbox"
                id="smsNotification"
                class="w-4 h-4 text-black bg-gray-100 border-gray-300 rounded focus:ring-gray-500"
              />
              <label
                for="smsNotification"
                class="ml-3 text-sm font-medium text-black"
              >
                SMS de notification
              </label>
            </div>
            <p class="text-sm text-black italic">
              Ne plus recevoir les notifications de nouvelle commande.
            </p>
            -->

            <div
              class="flex items-center p-3 bg-white rounded-lg border border-gray-200"
            >
              <input
                :checked="formData.shop.showMainColor"
                @change="
                  updateFormData(
                    'shop.showMainColor',
                    ($event.target as HTMLInputElement).checked,
                  )
                "
                type="checkbox"
                id="showMainColor"
                class="w-4 h-4 text-black bg-gray-100 border-gray-300 rounded focus:ring-gray-500"
              />
              <label
                for="showMainColor"
                class="ml-3 text-sm font-medium text-black"
              >
                Afficher couleur principale
              </label>
            </div>
            <p class="text-sm text-black italic">
              En cochant cette case, les produits du catalogue, seul les couleurs principal seront visible
            </p>
          </div>
        </div>

        <!-- Bouton Sauvegarder sous "Notifications et affichage", aligné à droite, descendu pour aligner son bas avec la carte "Informations bancaires" -->
        <div class="mt-10 hidden md:flex flex-col items-end gap-2">
          <button
            @click="$emit('save')"
            :disabled="saving || disableSave"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <i v-if="saving" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-save"></i>
            <span>{{ saving ? "Sauvegarde..." : "Sauvegarder" }}</span>
          </button>
          <p
            v-if="errorMessage"
            class="text-sm text-red-600 max-w-xl text-right"
          >
            {{ errorMessage }}
          </p>
        </div>
      </div>
    </div>
  </DataCard>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { DataCard, BaseInput } from "@/components";

import { catalogVisibilityOptions } from "@/data";

import type { GeneralInformationFormData } from "@/types";

const props = defineProps<{
  formData: GeneralInformationFormData;
  saving?: boolean;
  disableSave?: boolean;
  // Message d'erreur à afficher en cas d'échec de sauvegarde
  errorMessage?: string;
}>();

const emit = defineEmits<{
  (e: "update:formData", value: GeneralInformationFormData): void;
  (e: "validation-changed", hasErrors: boolean): void;
  (e: "save"): void;
}>();

// Afficher/masquer les champs de mot de passe
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);
const newPwdType = computed(() =>
  showNewPassword.value ? "text" : "password",
);
const confirmPwdType = computed(() =>
  showConfirmPassword.value ? "text" : "password",
);

// Validation des mots de passe
const passwordError = computed(() => {
  if (!props.formData.security.changePassword) return "";

  const password = props.formData.security.newPassword;
  if (!password) return "";

  if (password.length < 6) {
    return "Le mot de passe doit contenir au moins 6 caractères";
  }

  return "";
});

// Validation globale - détecte toutes les erreurs
const hasValidationErrors = computed(() => {
  // Erreurs de mots de passe
  const passwordErrors = passwordError.value || confirmPasswordError.value;

  // Erreurs IBAN (format invalide)
  const bankingFormatErrors = ibanHasError.value;

  // Erreurs de champs requis (IBAN et accountTitle obligatoires)
  const requiredFieldsErrors =
    !props.formData.lastName ||
    !props.formData.firstName ||
    !props.formData.email ||
    !props.formData.banking.iban ||
    !props.formData.banking.accountTitle;

  return !!(passwordErrors || bankingFormatErrors || requiredFieldsErrors);
});

const confirmPasswordError = computed(() => {
  if (!props.formData.security.changePassword) return "";

  const password = props.formData.security.newPassword;
  const confirmPassword = props.formData.security.confirmPassword;

  if (!confirmPassword) return "";

  if (password !== confirmPassword) {
    return "Les mots de passe ne correspondent pas";
  }

  return "";
});

// État local pour IBAN
const ibanValue = ref(props.formData.banking.iban);
const ibanHasError = ref(false);
const ibanIsValid = ref(false);
const ibanErrorMessage = ref("");

const accountTitleHasError = ref(false);

// Formatage automatique IBAN avec espaces
const formatIban = (value: string) => {
  const cleanValue = value.replace(/\s/g, "");
  return cleanValue.replace(/(.{4})/g, "$1 ").trim();
};

// Gestion IBAN avec blocage automatique
const handleIbanInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const value = target.value;

  const cleanValue = value.replace(/\s/g, "");

  // Blocage strict : si on a exactement 27 caractères, on ne peut plus taper
  if (cleanValue.length >= 27) {
    const finalFormatted = formatIban(cleanValue.substring(0, 27));
    ibanValue.value = finalFormatted;
    updateFormData("banking.iban", finalFormatted);
    validateIban();
    return;
  }

  // Applique le formatage seulement si on a au moins 4 caractères
  if (cleanValue.length >= 4) {
    const formattedValue = formatIban(value);
    ibanValue.value = formattedValue;
    updateFormData("banking.iban", formattedValue);
  } else {
    ibanValue.value = value;
    updateFormData("banking.iban", value);
  }

  validateIban();
};

// Validation IBAN
const validateIban = () => {
  const value = ibanValue.value.trim();

  if (!value) {
    ibanHasError.value = false;
    ibanIsValid.value = false;
    ibanErrorMessage.value = "";
    return;
  }

  const cleanValue = value.replace(/\s/g, "").toUpperCase();
  const ibanRegex =
      /^FR[0-9]{2}(?: ?[A-Z0-9]{4}){5} ?[A-Z0-9]{3}$/;
      // /^FR[0-9]{2}[A-Z0-9]{4}[A-Z0-9]{4}[A-Z0-9]{4}[A-Z0-9]{4}[A-Z0-9]{4}[A-Z0-9]{3}$/;

  if (cleanValue.length !== 27) {
    ibanHasError.value = true;
    ibanIsValid.value = false;
    ibanErrorMessage.value =
      "Format IBAN invalide. Exemple : FR76 3000 1007 9416 0000 0000 000";
    return;
  }

  if (!ibanRegex.test(cleanValue)) {
    ibanHasError.value = true;
    ibanIsValid.value = false;
    ibanErrorMessage.value =
      "Format IBAN invalide. Exemple : FR76 3000 1007 9416 0000 0000 000";
    return;
  }

  ibanHasError.value = false;
  ibanIsValid.value = true;
  ibanErrorMessage.value = "";
};

// Validation Titulaire du compte
const validateAccountTitle = () => {
  const value = props.formData.banking.accountTitle?.trim();
  if (!value) {
    accountTitleHasError.value = true;
  } else {
    accountTitleHasError.value = false;
  }
};

// Synchronisation avec les props (comme register)
watch(
  () => props.formData.banking.iban,
  (newValue) => {
    ibanValue.value = newValue;
    if (newValue) {
      validateIban();
    }
  },
  { immediate: true },
);

// Watcher pour émettre les changements de validation
watch(
  hasValidationErrors,
  (hasErrors) => {
    emit("validation-changed", hasErrors);
  },
  { immediate: true },
);

const updateFormData = (
  field: string,
  value: string | number | boolean | FileList | null | undefined,
) => {
  const updatedFormData = { ...props.formData };
  const keys = field.split(".");
  let current: Record<string, unknown> = updatedFormData;

  for (let i = 0; i < keys.length - 1; i++) {
    current = current[keys[i]] as Record<string, unknown>;
  }

  // Supprime les caractères invisibles et tout espace dans les emails
  const normalizeEmail = (email: string) =>
    email
      .replace(
        /[\u00A0\u1680\u180E\u2000-\u200D\u2028\u2029\u202F\u205F\u2060\u3000\uFEFF]/g,
        "",
      )
      .replace(/\s+/g, "")
      .trim();

  // Convertir les valeurs undefined en chaînes vides pour les champs texte
  let processedValue = value === undefined ? "" : value;
  if (
    (field === "email" || field === "emailSecondary") &&
    typeof processedValue === "string"
  ) {
    processedValue = normalizeEmail(processedValue);
  }
  current[keys[keys.length - 1]] = processedValue;
  emit("update:formData", updatedFormData);
};
</script>
